import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw } from 'vue-router';
import TabsPage from '../views/TabsPage.vue'
import LoginPage from '../views/Login.vue'
import Tab3Page from '../views/Tab3Page.vue'
import SuperAdminDashboard from '../views/SuperAdminDashboard.vue'
import AttributionDashboard from '../views/AttributionDashboard.vue'
import UserManagementView from '../views/UserManagementAltView.vue'
import ResetPassword from '../views/ResetPassword.vue'
import DashboardView from '../views/DashboardView.vue'
import { api } from '@/services/api'
import { supabase } from '@/supabase'
import { useUserStore } from '@/store/user'
import { alertController } from '@ionic/vue'
import SuperAdmin from '../views/SuperAdmin.vue'
const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Login',
    component: LoginPage,
    meta: { public: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { public: true }
  },
  {
    path: '/admin/dashboard',
    name: 'SuperAdminDashboard',
    component: SuperAdmin,
    meta: { 
      requiresSuperAdmin: true,
      // disableAutoRefresh: true
    }
  },
  {
    path: '/',
    redirect: '/tabs/tab1'
  },
  {
    path: '/tabs/',
    component: () => import('@/views/TabsPage.vue'),
    children: [
      {
        path: '',
        redirect: '/tabs/tab1'
      },
      {
        path: 'tab1',
        component: () => import('@/views/CustomerDashboard.vue')
      },
      {
        path: 'customer/:id?',
        component: () => import('@/views/CustomerPage.vue')
      },
      {
        path: 'tab2/:id?',
        redirect: to => {
          return `/tabs/customer${to.params.id ? '/' + to.params.id : ''}`
        }
      },
      {
        path: 'tab3',
        component: () => import('@/views/Tab3Page.vue')
      },
      {
        path: 'tab4',
        component: () => import('@/views/SuperAdminDashboard.vue'),
        meta: { 
          requiresSuperAdmin: true
        }
      },
      {
        path: 'users',
        component: UserManagementView,
        meta: { 
          requiresAdmin: true
        }
      },
      {
        path: 'dashboard',
        component: DashboardView,
        meta: {
          requiresSuperAdmin: true
        }
      }
    ]
  },
  {
    path: '/check-in',
    component: Tab3Page,
    meta: { public: true }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Always scroll to top
    return { top: 0 }
  }
})

// Update navigation guard
router.beforeEach(async (to, from, next) => {
  console.log('==== ROUTER NAVIGATION ====');
  console.log('From:', from.path);
  console.log('To:', to.path);
  console.log('URL:', window.location.href);
  console.log('Hash:', window.location.hash);
  console.log('Search:', window.location.search);

  // If we're already on the reset-password page, allow access
  if (to.path === '/reset-password') {
    console.log('👍 Already on reset-password page, allowing access');
    next();
    return;
  }

  // If this is a password reset link and we're not on the reset-password page
  const hash = window.location.hash;
  if (hash && hash.includes('type=recovery') && to.path !== '/reset-password') {
    console.log('🔐 Found password reset link, redirecting to reset-password page');
    next({
      path: '/reset-password',
      replace: true
    });
    return;
  }

  const userStore = useUserStore();
  const isPublicRoute = to.matched.some(record => record.meta.public);
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresSuperAdmin = to.matched.some(record => record.meta.requiresSuperAdmin);
  const requiresOwner = to.matched.some(record => record.meta.requiresOwner);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  const isAuthenticated = localStorage.getItem('sb-auth-token');
  
  // If this is a page refresh on the dashboard (not a navigation)
  // if (from.name === undefined && (to.path === '/admin/dashboard' || to.path === '/owner/dashboard' || to.path === '/admin/users')) {
  //   console.log('Dashboard refresh detected - allowing restoration');
  //   return next();
  // }

  // Check if route requires super admin access
  if (requiresSuperAdmin) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return next('/');
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_super_admin')
        .eq('id', user.id)
        .single();

      if (!profile?.is_super_admin) {
        return next('/tabs/tab1');
      }

      return next();
    } catch (error) {
      console.error('Error checking super admin status:', error);
      return next('/');
    }
  }

  // Check if route requires owner access
  if (requiresOwner) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user found, redirecting to login');
        return next('/');
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_owner, is_super_admin')
        .eq('id', user.id)
        .single();

      if (!profile?.is_owner && !profile?.is_super_admin) {
        console.log('User is not an owner or super admin, redirecting to tab1');
        return next('/tabs/tab1');
      }

      console.log('Owner access granted');
      return next();
    } catch (error) {
      console.error('Error checking owner status:', error);
      return next('/');
    }
  }

  // Check if route requires admin access
  if (requiresAdmin) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return next('/');
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin, is_super_admin')
        .eq('id', user.id)
        .single();

      if (!profile?.is_admin && !profile?.is_super_admin) {
        return next('/tabs/tab1');
      }

      return next();
    } catch (error) {
      console.error('Error checking admin status:', error);
      return next('/');
    }
  }

  // Check if we're going to Tab2 without an ID
  if (to.path === '/tabs/tab2' || to.path === '/tabs/tab2/') {
    try {
      const customerId = Array.isArray(to.params.id) ? to.params.id[0] : to.params.id
      const customers = await api.getCustomersList(customerId)
      if (customers && customers.length > 0) {
        return next(`/tabs/tab2/${customers[0].id}`)
      }
    } catch (error) {
      console.error('Error fetching customers:', error)
      return next('/tabs/tab1')
    }
  }

  // Check if we're going to the customer page without an ID
  if (to.path === '/tabs/customer' || to.path === '/tabs/customer/') {
    const alert = await alertController.create({
      header: 'Select a Customer',
      message: "Please select a customer from Today's Action to view their details.",
      cssClass: 'force-light-alert',
      buttons: [
        {
          text: 'OK',
          handler: () => {
            next('/tabs/tab1');
          }
        }
      ]
    });
    await alert.present();
    return;
  }

  if (requiresAuth && !isAuthenticated) {
    next('/check-in')
  } else {
    next()
  }
})

// Add navigation logging in development
if (import.meta.env.DEV) {
  router.beforeEach((to, from, next) => {
    console.log('==== ROUTER NAVIGATION ====')
    console.log('From:', from.path)
    console.log('To:', to.path)
    console.log('URL:', to.fullPath)
    console.log('Hash:', to.hash)
    console.log('Search:', to.query)
    next()
  })
}

export default router

